import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout, { adminFetch, SectionTitle, AdminCard, Badge, useAdmin } from './AdminLayout';

/**
 * AdminUtilisateurs — gestion CRUD des utilisateurs Aidly (Admin Users
 * CRUD sprint, 2026-05-28). Remplace l'ancienne vue lecture seule.
 *
 * Endpoints utilisés (tous protégés adminAuth backend) :
 *   - GET    /api/auth/users               liste
 *   - POST   /api/auth/users               create {email, password, role}
 *   - PUT    /api/auth/users/:id           update {email?, role?, active?}
 *   - PATCH  /api/auth/users/:id/password  reset password {password}
 *   - DELETE /api/auth/users/:id           hard delete
 *
 * Garde-fous self-action gérés côté backend (ADMIN_SELF_DEGRADE /
 * SELF_DEACTIVATE / SELF_DELETE) — le frontend affiche le message
 * d'erreur retourné. Pas de check côté UI car l'AuthContext n'expose
 * pas l'email/id de l'admin courant (extension future si besoin).
 */
export default function AdminUtilisateurs() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    adminFetch('/api/auth/users')
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => setUsers(Array.isArray(data.users) ? data.users : []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = () => setModal(null);
  const onModalSuccess = () => { closeModal(); load(); };

  return (
    <AdminLayout title="Utilisateurs">
      <Content
        users={users}
        loading={loading}
        loadError={loadError}
        onRetry={load}
        onCreate={() => setModal({ type: 'create' })}
        onEdit={(u) => setModal({ type: 'edit', user: u })}
        onReset={(u) => setModal({ type: 'reset', user: u })}
        onDelete={(u) => setModal({ type: 'delete', user: u })}
      />
      {modal?.type === 'create' && <CreateModal onClose={closeModal} onSuccess={onModalSuccess} />}
      {modal?.type === 'edit' && <EditModal user={modal.user} onClose={closeModal} onSuccess={onModalSuccess} />}
      {modal?.type === 'reset' && <ResetModal user={modal.user} onClose={closeModal} onSuccess={onModalSuccess} />}
      {modal?.type === 'delete' && <DeleteModal user={modal.user} onClose={closeModal} onSuccess={onModalSuccess} />}
    </AdminLayout>
  );
}

function Content({ users, loading, loadError, onRetry, onCreate, onEdit, onReset, onDelete }) {
  const { dark, border } = useAdmin();
  const textColor = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  if (loading) return <p style={{ color: mutedColor, padding: 16 }}>Chargement…</p>;
  if (loadError) {
    return (
      <div style={{
        padding: 16, borderRadius: 10, background: '#FDECEA',
        border: '1px solid rgba(192,57,43,0.3)', color: '#933',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <span>Erreur de chargement. Vérifiez votre connexion.</span>
        <button onClick={onRetry} style={btnPrimary}>Réessayer</button>
      </div>
    );
  }

  return (
    <div>
      <AdminCard style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <SectionTitle>Utilisateurs ({users.length})</SectionTitle>
          <button onClick={onCreate} style={btnPrimary}>+ Nouvel utilisateur</button>
        </div>
        <p style={{ fontSize: 13, color: mutedColor, marginBottom: 16, lineHeight: 1.6 }}>
          Comptes admin et agent d'Aidly. Création, édition, désactivation et
          suppression. Un admin ne peut pas agir sur son propre compte (sécurité).
        </p>

        {users.length === 0 ? (
          <p style={{ fontSize: 14, color: mutedColor }}>
            Aucun utilisateur. Cliquez sur « + Nouvel utilisateur » pour en créer un.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${border}` }}>
                  {['Email', 'Rôle', 'Statut', 'Créé le', 'Actions'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: mutedColor, fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${border}` }}>
                    <td style={{ padding: '12px', color: textColor }}>{u.email}</td>
                    <td style={{ padding: '12px' }}>
                      <Badge color={u.role === 'admin' ? '#D4724A' : '#2980B9'}>{u.role}</Badge>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Badge color={u.active ? '#3D8B5E' : '#8A8A8A'}>
                        {u.active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px', color: mutedColor, fontSize: 13 }}>
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button onClick={() => onEdit(u)} style={btnSecondary}>Éditer</button>
                        <button onClick={() => onReset(u)} style={btnSecondary}>Mot de passe</button>
                        <button onClick={() => onDelete(u)} style={btnDanger}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>
    </div>
  );
}

// ---------- Modals ----------

function CreateModal({ onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('agent');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminFetch('/api/auth/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, role }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Erreur ${res.status}`);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Nouvel utilisateur" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Field label="Email">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} placeholder="prenom.nom@aidly.fr" autoFocus />
        </Field>
        <Field label="Mot de passe (≥ 8 caractères)">
          <input type="text" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} placeholder="à transmettre à l'agent" />
        </Field>
        <Field label="Rôle">
          <select value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle}>
            <option value="agent">Agent (call-center)</option>
            <option value="admin">Admin (gestion plateforme)</option>
          </select>
        </Field>
        {error && <ErrorBanner message={error} />}
        <ModalActions onClose={onClose} submitting={submitting} submitLabel="Créer" />
      </form>
    </Modal>
  );
}

function EditModal({ user, onClose, onSuccess }) {
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);
  const [active, setActive] = useState(user.active);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = {};
      if (email !== user.email) body.email = email;
      if (role !== user.role) body.role = role;
      if (active !== user.active) body.active = active;
      const res = await adminFetch(`/api/auth/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const respBody = await res.json().catch(() => ({}));
        throw new Error(respBody.message || `Erreur ${res.status}`);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Éditer ${user.email}`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Field label="Email">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Rôle">
          <select value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle}>
            <option value="agent">Agent</option>
            <option value="admin">Admin</option>
          </select>
        </Field>
        <Field label="Statut">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>Compte actif (l'utilisateur peut se connecter)</span>
          </label>
        </Field>
        {error && <ErrorBanner message={error} />}
        <ModalActions onClose={onClose} submitting={submitting} submitLabel="Enregistrer" />
      </form>
    </Modal>
  );
}

function ResetModal({ user, onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminFetch(`/api/auth/users/${user.id}/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Erreur ${res.status}`);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Nouveau mot de passe — ${user.email}`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <p style={{ fontSize: 13, color: '#8A8A8A', marginBottom: 12, lineHeight: 1.6 }}>
          Le nouveau mot de passe remplace immédiatement l'ancien. Transmettez-le
          à l'utilisateur hors-band (téléphone, email personnel).
        </p>
        <Field label="Nouveau mot de passe (≥ 8 caractères)">
          <input type="text" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} autoFocus />
        </Field>
        {error && <ErrorBanner message={error} />}
        <ModalActions onClose={onClose} submitting={submitting} submitLabel="Réinitialiser" />
      </form>
    </Modal>
  );
}

function DeleteModal({ user, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminFetch(`/api/auth/users/${user.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Erreur ${res.status}`);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canDelete = confirm === user.email;

  return (
    <Modal title="Supprimer l'utilisateur" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <p style={{ fontSize: 14, color: '#933', marginBottom: 12, lineHeight: 1.6, background: '#FDECEA', padding: 10, borderRadius: 8 }}>
          ⚠️ Cette action est <strong>irréversible</strong>. Pour préserver l'historique
          d'audit, préférer désactiver le compte (Éditer → décocher « Compte actif »).
        </p>
        <Field label={`Pour confirmer, retape l'email : ${user.email}`}>
          <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={inputStyle} autoFocus />
        </Field>
        {error && <ErrorBanner message={error} />}
        <ModalActions onClose={onClose} submitting={submitting} submitLabel="Supprimer définitivement" disabled={!canDelete} danger />
      </form>
    </Modal>
  );
}

// ---------- Modal helpers ----------

function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 24, maxWidth: 460, width: '100%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#2D2D2D' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#8A8A8A', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2D2D2D', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ onClose, submitting, submitLabel, disabled, danger }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
      <button type="button" onClick={onClose} style={btnSecondary} disabled={submitting}>Annuler</button>
      <button type="submit" disabled={submitting || disabled} style={danger ? btnDanger : btnPrimary}>
        {submitting ? '…' : submitLabel}
      </button>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div style={{ padding: 10, borderRadius: 8, background: '#FDECEA', color: '#933', fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
      {message}
    </div>
  );
}

// ---------- Styles ----------

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 7,
  border: '1.5px solid #D9D9D9', fontSize: 14, boxSizing: 'border-box',
  fontFamily: 'inherit', outline: 'none',
};

const btnPrimary = {
  padding: '8px 16px', borderRadius: 8, border: 'none',
  background: '#D4724A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnSecondary = {
  padding: '6px 12px', borderRadius: 7, border: '1px solid #D9D9D9',
  background: '#fff', color: '#2D2D2D', fontSize: 12, fontWeight: 500, cursor: 'pointer',
};

const btnDanger = {
  padding: '6px 12px', borderRadius: 7, border: 'none',
  background: '#C0392B', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
